import { useCallback, useState } from "react";
import { Chip, Collapse, Paper, Stack } from "@mui/material";
import {
  CollectionSelect,
  type CollectionSelectOption,
} from "@/components/CollectionSelect/CollectionSelect";
import { EXPORT_INCLUSION_TYPES } from "../types";

export const useIntentCollectionRenderer = (
  inclusion: EXPORT_INCLUSION_TYPES,
  selectedCollectionIds: string[],
  onSelectionChange: (ids: string[]) => void
) => {
  const [labelsById, setLabelsById] = useState<Record<string, string>>({});

  const handleSelect = useCallback(
    (option: CollectionSelectOption) => {
      const id = String(option.value);
      setLabelsById((prev) => ({ ...prev, [id]: option.label }));
      if (!selectedCollectionIds.includes(id)) {
        onSelectionChange([...selectedCollectionIds, id]);
      }
    },
    [selectedCollectionIds, onSelectionChange]
  );

  const handleRemove = useCallback(
    (id: string) => {
      onSelectionChange(
        selectedCollectionIds.filter((existing) => existing !== id)
      );
    },
    [selectedCollectionIds, onSelectionChange]
  );

  return useCallback(
    (optionValue: string) => {
      if (optionValue !== EXPORT_INCLUSION_TYPES.SELECTED_COLLECTIONS)
        return null;

      return (
        <Collapse
          in={inclusion === EXPORT_INCLUSION_TYPES.SELECTED_COLLECTIONS}
          timeout={300}
          unmountOnExit
        >
          <Paper variant="outlined" sx={{ mt: 1, p: 2, mr: 3 }}>
            <Stack spacing={2}>
              <CollectionSelect
                type="intent"
                placeholder="Search Intent Collections"
                onSelect={handleSelect}
              />
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {selectedCollectionIds.map((id) => (
                  <Chip
                    key={id}
                    label={labelsById[id] ?? id}
                    onDelete={() => handleRemove(id)}
                    data-testid={`intent-collection-chip-${id}`}
                  />
                ))}
              </Stack>
            </Stack>
          </Paper>
        </Collapse>
      );
    },
    [inclusion, selectedCollectionIds, labelsById, handleSelect, handleRemove]
  );
};
